import { Check, Copy, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAudienceGeneratePublicLink } from '@/hooks/useGenerateAudiencePublicLink';
import { Audience } from '@/services/AudiencesService';

interface PublicLinkProps {
  audience: Pick<Audience, 'id' | 'opaqueId'>;
}

export function PublicLink({ audience }: PublicLinkProps) {
  const [isCopied, setIsCopied] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Behaviors.audienceCard',
  });

  const { generatePublicLink, isLoading } = useAudienceGeneratePublicLink(
    audience.id,
  );

  const publicLink = audience.opaqueId
    ? `https://products.fashionai.dev/${audience.opaqueId}/${audience.id}`
    : null;

  async function handleGeneratePublicLink() {
    try {
      await generatePublicLink();

      toast.success(t('generateLinkSuccess'));
    } catch (error) {
      toast.error(t('generateLinkError'));
    }
  }

  async function handleCopyPublicLink() {
    if (!publicLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(publicLink);
      setIsCopied(true);
      toast.success(t('copySuccess'));
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast.error(t('copyError'));
    }
  }

  return (
    <div className="mt-6 space-y-2">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-medium text-gray-600">
          {t('publicLinkLabel')}
        </p>

        {!publicLink && (
          <Button
            size="sm"
            onClick={handleGeneratePublicLink}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('generatingLinkButtonLabel')}
              </>
            ) : (
              t('generateLinkButtonLabel')
            )}
          </Button>
        )}
      </div>

      {publicLink && (
        <div className="flex items-center gap-2">
          <Input value={publicLink} readOnly />
          <Button size="icon" variant="outline" onClick={handleCopyPublicLink}>
            {isCopied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
